import _ from "lodash";
/**--external-- */
import React, { useEffect } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";

/**--internal-- */
import { getFilterOptionsQuery } from "../../../modules/Queries";
import { getFilterOptionsFromCache } from "../../../modules/GraphqlHelpers";
import {
  getFilterOptionParam,
  getFilterOptions,
  getFilterDisabilityStatusConfig,
  getFilterParam,
  getValidFilterValuesStatus,
  getPortfolioFolderFilterTooltip,
} from "../../../modules/Utils";
import { getProjectObjectivesLabelMapping } from "ProgressReportCommon/utils";
import { colors } from "Constants";
import {
  FilterLabelButton,
  ChecklistDropdown,
  Tooltip,
} from "@toddle-edu/ds-web";
import { FolderFilled } from "@toddle-edu/ds-icons";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getLabelValueForMultiFilterLabelButton,
  getEntitySubType,
} from "Utils";

import FilterDropdown from "UIComponents/FilterDropdown";
import FilterList from "UIComponents/FilterList";
import Level3Filter from "UIComponents/Level3Filter";
import {
  filterSearchStyle,
  filterStyles,
  parentContainerStyle,
} from "./DynamicFilterStyles";
import { useI18n } from "Hooks";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";

import ButtonComponent from "./ButtonComponent";
import {
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_PERSONAL_OBJECTIVES,
  ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
} from "Constants/stringConstants";

const DynamicFilter = props => {
  const {
    options,
    isLoading,
    isData,
    isFilterVisible,
    containerParentRef,
    values,
    isCheckList,
    label,
    levels,
    entityType,
    parentFiltersData,
    isParent,
    dependentFilterParents,
    buttonDropDownContainerStyle,
    updateLoadingFilters,
    updateInputField,
    updateFilterOptions,
    markDependentFilterAsUpdated,
    parentFilterObj,
    isCompactUINeeded,
    projectNodeDetails,
  } = props;

  const totalOptions = _.size(options);

  const isDependent = !_.isEmpty(dependentFilterParents);

  useEffect(() => {
    return () => {
      /**Clear pending filters when component is unmounted */
      updateLoadingFilters({ isReady: true, filter: entityType });
    };
  }, []);

  useEffect(() => {
    if (_.isEmpty(parentFilterObj) || !isDependent) {
      return;
    }

    const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

    if (!_.includes(dependentFilters, entityType)) {
      return;
    }

    updateInputField({ values: [], key: entityType });

    markDependentFilterAsUpdated({ filter: entityType });
  }, [parentFilterObj]);

  /**update filter options when old data from cache is available,
   * latest data is available.
   * By updating options this way, user can see options early,
   * if they are available in cache.  */
  useEffect(() => {
    if (!isLoading) {
      const { areValuesUpdated, updatedValues } = getValidFilterValuesStatus({
        options,
        values,
        entityType,
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      });

      if (areValuesUpdated) {
        updateInputField({ values: updatedValues, key: entityType, isParent });
      }
    }

    updateFilterOptions({
      filterOptions: options,
      filter: entityType,
    });

    updateLoadingFilters({ filter: entityType, isReady: isData });
  }, [isLoading]);

  const { t } = useI18n(["snp", "common"]);

  /**If this filter is part of more filters, then it will be queried, but it won't
   * be shown unless it is selected. For other filters, they are always visible.
   */
  if (!isFilterVisible) return null;

  const filterDisabilityStatusConfig = getFilterDisabilityStatusConfig({
    parentFiltersData,
    config: dependentFilterParents,
    totalOptions,
    isLoading,
    levels,
  });

  const getAllOptionLabelForFilterList = ({ label1, label2 }) => {
    switch (entityType) {
      case "COURSE": {
        return t("snp:all_label1_label2", {
          label1,
          label2,
        });
      }

      case ELEMENT_TYPE_MYP_OBJECTIVES: {
        return t("common:all_label", { label: label1 });
      }
    }
  };

  const getUseDsTooltip = () => {
    switch (entityType) {
      case "EVIDENCE_FOLDER": {
        return true;
      }
      default: {
        return false;
      }
    }
  };
  if (entityType === "EVIDENCE_FOLDER") {
    const updatedOptions = _.map(options, option => {
      return {
        ...option,
        prefixElement: <FolderFilled style={{ color: option.color }} />,
      };
    });

    return (
      <ConditionalWrapper
        condition={filterDisabilityStatusConfig.isDisabled}
        wrapper={
          <Tooltip
            tooltip={getPortfolioFolderFilterTooltip({
              t,
              filterDisabilityStatusConfig,
              label,
            })}
            align={{ offset: [0, -10] }}
          />
        }
        type={"wrapperAsElement"}
      >
        <ChecklistDropdown
          label={label}
          value={values || []}
          prefixElement={<FolderFilled style={{ color: colors.blue400 }} />}
          onChange={values =>
            updateInputField({ values, key: entityType, isParent })
          }
          showAllOption={true}
          allOptionLabel={t("journal:all_folders")}
          isSearchEnabled={false}
          allOptionPrefix={<FolderFilled style={{ color: colors.blue400 }} />}
          showSelectedOptionsOnTop={false}
          disabled={filterDisabilityStatusConfig.isDisabled}
          options={updatedOptions}
          dsVersion={isCompactUINeeded ? "2.0" : "1.0"}
        >
          <FilterLabelButton
            label={label}
            variant="plain"
            labelValue={getLabelValueForMultiFilterLabelButton({
              t,
              options: updatedOptions,
              selectedOptions: values || [],
            })}
          />
        </ChecklistDropdown>
      </ConditionalWrapper>
    );
  }

  if (levels === 1) {
    return (
      <FilterDropdown
        label={label}
        searchBarProps={{
          ...filterSearchStyle,
          placeholder: isCompactUINeeded
            ? t("common:search")
            : t("common:search_label", {
                label,
              }),
        }}
        buttonComponent={
          <ButtonComponent
            t={t}
            label={label}
            entityType={entityType}
            values={values}
            options={options}
            filterDisabilityStatusConfig={filterDisabilityStatusConfig}
            containerParentRef={containerParentRef}
            isCheckList={isCheckList}
            useDsTooltip={getUseDsTooltip()}
          />
        }
        showAllOption={isCheckList}
        isCheckList={isCheckList}
        options={options}
        updateInputField={values =>
          updateInputField({ values, key: entityType, isParent })
        }
        value={values}
        isDisabled={filterDisabilityStatusConfig.isDisabled}
        buttonDropDownContainerStyle={{
          ...filterStyles.buttonDropDownContainerStyle,
          ...buttonDropDownContainerStyle,
        }}
        checkListContainerStyle={{ ...filterStyles.checkListContainerStyle }}
        showSearchBar={true}
        parentContainerStyle={parentContainerStyle}
        checkListItemStyle={filterStyles.checkListItemStyle}
        checkboxItemStyle={filterStyles.checkboxItemStyle}
        checkBoxParentContainer={filterStyles.checkBoxParentContainer}
        isMatchingTextEnabled={true}
        isSearchTextInState={true}
        showArchivedOptionUI={true}
      />
    );
  }

  if (entityType == ELEMENT_TYPE_MYP_OBJECTIVES) {
    return (
      <Level3Filter
        optionList={options}
        updateInputField={({ updatedValueList }) => {
          updateInputField({
            key: entityType,
            values: updatedValueList,
            isParent,
          });
        }}
        buttonDropDownContainerStyle={buttonDropDownContainerStyle}
        getSearchPlaceholder={({ label }) =>
          t("common:search_label", { label })
        }
        getAllOptionLabel={({ label }) => t("common:all_label", { label })}
        buttonElement={
          <ButtonComponent
            t={t}
            entityType={entityType}
            filterDisabilityStatusConfig={filterDisabilityStatusConfig}
            containerParentRef={containerParentRef}
          />
        }
        valueList={values}
        label={label}
        isDisabled={filterDisabilityStatusConfig.isDisabled}
      />
    );
  }

  return (
    <FilterList
      updateFilterList={({ updatedValueList }) => {
        updateInputField({
          key: entityType,
          values: updatedValueList,
          isParent,
        });
      }}
      getAllOptionLabelForFilter={getAllOptionLabelForFilterList}
      getSearchPlaceholder={({ label }) => t("common:search_label", { label })}
      valueList={values || []}
      optionList={options || []}
      label={label}
      onUpdateShowDropDown={() => {}}
      buttonElement={
        <ButtonComponent
          t={t}
          entityType={entityType}
          filterDisabilityStatusConfig={filterDisabilityStatusConfig}
          containerParentRef={containerParentRef}
          isCheckList={true}
        />
      }
      buttonDropDownContainerStyle={buttonDropDownContainerStyle}
      isDisabled={filterDisabilityStatusConfig.isDisabled}
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const {
    entityType,
    parentFiltersData,
    curriculumProgramId,
    parentFilterObj,
    dependentFilterParents,
    curriculumType,
    projectNodeDetails,
  } = ownProps;

  const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

  const currentParentFilters = _.map(
    dependentFilterParents,
    ({ parent }) => parent
  );

  const commonFilters = _.intersection(dependentFilters, currentParentFilters);

  const optionParam = getFilterOptionParam({
    entityType,
    parentFiltersData,
    curriculumProgramId,
    curriculumType,
  });

  const filterParam = getFilterParam({ entityType, parentFiltersData });

  let filterParamWithEntitySubType = {};

  if (
    _.includes(
      [
        ELEMENT_TYPE_MYP_OBJECTIVES,
        ELEMENT_TYPE_PERSONAL_OBJECTIVES,
        ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
      ],
      entityType
    )
  ) {
    filterParamWithEntitySubType = getFilterParam({
      entityType,
      parentFiltersData,
      entitySubType: getEntitySubType({
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      }),
    });
  }

  return {
    id: organizationId,
    optionParam,
    shouldSkipQuery: !_.isEmpty(commonFilters),
    filterOptions: [],
    filterParam,
    isLoading: true,
    isData: false,
    filterParamWithEntitySubType,
    defaultNameSortPreference: getDefaultNameSortPreference(state),
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getFilterOptionsQuery, {
    name: "getFilterOptionsQuery",
    alias: "getFilterOptionsQuery",
    skip: ({ shouldSkipQuery }) => shouldSkipQuery,
    options: ({
      baseSetType,
      entityType,
      id,
      optionParam,
      isProjectNode,
      filterParam,
      filterParamWithEntitySubType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          entityType: baseSetType ? baseSetType : entityType,
          id,
          options: optionParam,
          filters: isProjectNode ? filterParamWithEntitySubType : filterParam,
        },
      };
    },
    props: ({
      getFilterOptionsQuery: { variables, networkStatus },
      ownProps: {
        entityType,
        levels,
        attributes,
        defaultNameSortPreference,
        isProjectNode,
        projectNodeDetails,
      },
    }) => {
      const data = getFilterOptionsFromCache(variables);

      let updatedOptions = [];

      const isCachedDataAvailable = !_.isEmpty(data);

      if (isCachedDataAvailable) {
        updatedOptions = getFilterOptions({
          data,
          levels,
          attributes,
          entityType,
          defaultNameSortPreference,
          projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
        });
      }

      if (isProjectNode && entityType === ELEMENT_TYPE_MYP_OBJECTIVES) {
        const projectNodeUID = _.get(projectNodeDetails, "projectNodeUID", "");
        updatedOptions =
          projectNodeUID === "PP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "PERSONAL",
                }),
              }))
            : projectNodeUID === "CP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "COMMUNITY",
                }),
              }))
            : updatedOptions;
      }

      return {
        isData: !_.isEmpty(data),
        isLoading: _.includes([1, 2], networkStatus),
        options: updatedOptions,
      };
    },
  }),
  graphql(getFilterOptionsQuery, {
    name: "getFilterOptionsQuery",
    alias: "getFilterOptionsQuery(Interdisciplinary Objectives)",
    skip: ({ entityType, isProjectNode }) => {
      return entityType != ELEMENT_TYPE_MYP_OBJECTIVES || isProjectNode;
    },
    options: ({
      baseSetType,
      entityType,
      id,
      optionParam,
      filterParamWithEntitySubType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          entityType: baseSetType ? baseSetType : entityType,
          id,
          options: optionParam,
          filters: filterParamWithEntitySubType,
        },
      };
    },
    props: ({ getFilterOptionsQuery, ownProps }) => {
      const { networkStatus } = getFilterOptionsQuery;

      const {
        baseSetType,
        entityType,
        id,
        optionParam,
        levels,
        attributes,
        isLoading,
        isData,
        options,
        filterParamWithEntitySubType,
      } = ownProps;

      const data = getFilterOptionsFromCache({
        entityType: baseSetType ? baseSetType : entityType,
        options: optionParam,
        id,
        filters: filterParamWithEntitySubType,
      });

      let updatedOptions = [];

      const isCachedDataAvailable = !_.isEmpty(data);

      if (isCachedDataAvailable) {
        updatedOptions = getFilterOptions({
          data,
          levels,
          attributes,
          entityType,
        });
      }

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        options: _.isEmpty(options)
          ? updatedOptions
          : [...options, ...updatedOptions],
      };
    },
  })
)(DynamicFilter);

DynamicFilter.defaultProps = {
  isFilterVisible: true,
};
